import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { 
  corpAuthCookies, 
  TokenRefreshManager,
  createAuthFetch,
  type TokenData 
} from '@ecx/auth-client'

/**
 * Store de autenticação do Corp
 * 
 * Lê a sessão dos cookies compartilhados definidos pelo Auth
 * Gerencia refresh automático de tokens
 * Não gerencia login diretamente - redireciona para o Auth quando necessário
 *
 * TTL: access JWT no corp-api (JwtService, típico 15 min). Refresh token: validade
 * definida pelo serviço ECX Sessions — o corp-api apenas repassa POST /sessions/refresh.
 */

// URL da API para refresh token
const getRefreshUrl = () => {
  const apiUrl = import.meta.env.VITE_API_URL || 'https://api-corp.ecx.local'
  return `${apiUrl}/sessions/refresh`
}

const getLogoutUrl = () => {
  const apiUrl = import.meta.env.VITE_API_URL || 'https://api-corp.ecx.local'
  return `${apiUrl}/sessions/logout`
}

// Inatividade sem interação: após esse tempo, se o access expirar, encerra sessão (alinhado a ~2h de uso)
const INACTIVITY_TIMEOUT = 2 * 60 * 60 // 7200 segundos (2 horas)

// Rotas viewer por token (webview): públicas e sem sessão por design.
// redirectToLogin é no-op nelas — um 401 acidental nunca deve chutá-las ao login.
const PUBLIC_VIEWER_PATHS = ['/app/dados-cartao', '/swap-virtual-card']

export const useAuthStore = defineStore('corp-auth', () => {
  // State
  const accessToken = ref<string>('')
  const refreshToken = ref<string>('')
  const accountId = ref<string | null>(null)
  const businessCnpj = ref<string | null>(null)
  const expiresAt = ref<Date | null>(null)
  const isLoading = ref(true)
  const isRefreshing = ref(false)
  
  // Token Refresh Manager (singleton)
  let tokenRefreshManager: TokenRefreshManager | null = null
  
  // Auth Fetch instance
  let authFetchInstance: ReturnType<typeof createAuthFetch> | null = null
  
  // Getters
  const isAuthenticated = computed(() => {
    if (!accessToken.value || !expiresAt.value) return false
    return expiresAt.value > new Date()
  })
  
  const tokenData = computed((): TokenData | null => {
    if (!accessToken.value || !refreshToken.value || !expiresAt.value) return null
    return {
      accessToken: accessToken.value,
      refreshToken: refreshToken.value,
      expiresAt: expiresAt.value,
      accountId: accountId.value,
      businessCnpj: businessCnpj.value,
    }
  })
  
  /**
   * Atualiza o state com os dados do token.
   * Preserva accountId quando a resposta do refresh não inclui conta (evita perder conta ao dar refresh).
   */
  const updateTokens = (tokens: TokenData) => {
    accessToken.value = tokens.accessToken
    refreshToken.value = tokens.refreshToken
    const newAccountId = tokens.accountId ?? null
    accountId.value = newAccountId !== null && newAccountId !== '' ? newAccountId : accountId.value
    expiresAt.value = tokens.expiresAt instanceof Date 
      ? tokens.expiresAt 
      : new Date(tokens.expiresAt)
    
    const effectiveTokens: TokenData = {
      ...tokens,
      accountId: accountId.value,
      businessCnpj: businessCnpj.value,
    }
    corpAuthCookies.updateTokens(effectiveTokens)
    if (tokenRefreshManager) {
      tokenRefreshManager.updateTokens(effectiveTokens)
    }
  }
  
  /**
   * Inicializa o gerenciador de refresh token
   */
  const initRefreshManager = () => {
    if (tokenRefreshManager) return
    
    tokenRefreshManager = new TokenRefreshManager({
      refreshUrl: getRefreshUrl(),
      refreshBeforeExpiry: 300, // 5 minutos antes
      checkInterval: 60000, // 1 minuto
      inactivityTimeout: INACTIVITY_TIMEOUT,
      onRefresh: (tokens) => {
        console.log('[CorpAuth] Token refreshed successfully')
        updateTokens(tokens)
      },
      onRefreshFailed: (error) => {
        console.error('[CorpAuth] Token refresh failed:', error)
        redirectToLogin()
      },
      onInactivityTimeout: () => {
        console.log('[CorpAuth] Session expired due to inactivity')
        redirectToLogin()
      },
      getAdditionalHeaders: () => {
        const headers: Record<string, string> = {}
        if (accountId.value) {
          headers['X-ACCOUNT-ID'] = accountId.value
        }
        return headers
      },
    })
  }
  
  /**
   * Completa o fluxo de "criar senha" (ativação do primeiro gestor).
   * Salva tokens no state/cookie e inicia o ciclo de refresh.
   */
  const completeCreatePassword = (tokens: TokenData) => {
    updateTokens(tokens)
    initRefreshManager()
    if (tokenRefreshManager) {
      tokenRefreshManager.start(tokens)
    }
  }

  const applyValidSessionFromCookie = (cookieData: TokenData) => {
    const expires = cookieData.expiresAt instanceof Date
      ? cookieData.expiresAt
      : new Date(cookieData.expiresAt)
    accessToken.value = cookieData.accessToken
    refreshToken.value = cookieData.refreshToken
    accountId.value = cookieData.accountId || null
    businessCnpj.value = cookieData.businessCnpj ?? null
    expiresAt.value = expires
    initRefreshManager()
    tokenRefreshManager?.start(cookieData)
  }

  /**
   * Hidrata sessão a partir do cookie: se o access estiver expirado, aguarda refresh antes de retornar.
   * Deve ser usada no router (await) para não redirecionar ao login antes do refresh concluir.
   * @param options.skipExpiredCorpSessionRefresh — não tenta refresh quando o access expirou (rotas públicas com token SWAP na query).
   */
  const hydrateSession = async (
    options?: { skipExpiredCorpSessionRefresh?: boolean }
  ): Promise<boolean> => {
    isLoading.value = true
    const skipExpiredCorpSessionRefresh = options?.skipExpiredCorpSessionRefresh === true

    try {
      const cookieData = corpAuthCookies.getTokenData()

      if (!cookieData) {
        isLoading.value = false
        return false
      }

      const expires = cookieData.expiresAt instanceof Date
        ? cookieData.expiresAt
        : new Date(cookieData.expiresAt)

      if (expires > new Date()) {
        applyValidSessionFromCookie(cookieData)
        isLoading.value = false
        return true
      }

      if (skipExpiredCorpSessionRefresh || !cookieData.refreshToken) {
        isLoading.value = false
        return false
      }

      refreshToken.value = cookieData.refreshToken
      accountId.value = cookieData.accountId || null
      businessCnpj.value = cookieData.businessCnpj ?? null

      initRefreshManager()
      if (!tokenRefreshManager) {
        isLoading.value = false
        return false
      }

      tokenRefreshManager.updateTokens(cookieData)
      isRefreshing.value = true
      let newTokens: TokenData | null = null
      try {
        newTokens = await tokenRefreshManager.refresh(true)
      } finally {
        isRefreshing.value = false
        isLoading.value = false
      }

      if (!newTokens) {
        clearSession()
        return false
      }

      const hydrated: TokenData = {
        accessToken: accessToken.value,
        refreshToken: refreshToken.value,
        expiresAt: expiresAt.value!,
        accountId: accountId.value,
        businessCnpj: businessCnpj.value,
      }
      tokenRefreshManager.start(hydrated)
      return true
    } catch (error) {
      console.error('[CorpAuth] Error hydrating session:', error)
      isLoading.value = false
      return false
    }
  }

  /**
   * Lê apenas sessão ainda válida (access não expirado). Com access expirado, retorna false — use hydrateSession.
   */
  const loadSession = (): boolean => {
    isLoading.value = true

    try {
      const cookieData = corpAuthCookies.getTokenData()

      if (!cookieData) {
        isLoading.value = false
        return false
      }

      const expires = cookieData.expiresAt instanceof Date
        ? cookieData.expiresAt
        : new Date(cookieData.expiresAt)

      if (expires <= new Date()) {
        isLoading.value = false
        return false
      }

      applyValidSessionFromCookie(cookieData)
      isLoading.value = false
      return true
    } catch (error) {
      console.error('[CorpAuth] Error loading session:', error)
      isLoading.value = false
      return false
    }
  }
  
  /**
   * Força um refresh do token
   */
  const forceRefresh = async (): Promise<boolean> => {
    if (!tokenRefreshManager || !refreshToken.value) {
      return false
    }
    
    isRefreshing.value = true
    
    try {
      const newTokens = await tokenRefreshManager.refresh()
      return !!newTokens
    } finally {
      isRefreshing.value = false
    }
  }
  
  /**
   * Define o businessCnpj e persiste no cookie
   */
  const setBusinessCnpj = (cnpj: string | null) => {
    businessCnpj.value = cnpj
    
    if (accessToken.value && refreshToken.value && expiresAt.value) {
      corpAuthCookies.updateTokens({
        accessToken: accessToken.value,
        refreshToken: refreshToken.value,
        expiresAt: expiresAt.value,
        accountId: accountId.value,
        businessCnpj: cnpj,
      })
    }
  }

  /**
   * Define o accountId e atualiza o cookie
   */
  const setAccountId = (newAccountId: string) => {
    accountId.value = newAccountId
    
    if (accessToken.value && refreshToken.value && expiresAt.value) {
      corpAuthCookies.updateTokens({
        accessToken: accessToken.value,
        refreshToken: refreshToken.value,
        expiresAt: expiresAt.value,
        accountId: newAccountId,
        businessCnpj: businessCnpj.value,
      })
    }
  }
  
  /**
   * Limpa a conta atual mantendo a sessão, para reabrir a seleção de empresa
   * sem deslogar (usado no "escolher outra empresa" das telas de espera).
   */
  const clearAccount = () => {
    setAccountId('')
    setBusinessCnpj(null)
  }

  /**
   * Verifica se tem conta selecionada
   */
  const hasAccountSelected = computed(() => !!accountId.value)
  
  /**
   * Limpa a sessão (logout)
   */
  const clearSession = () => {
    accessToken.value = ''
    refreshToken.value = ''
    accountId.value = null
    businessCnpj.value = null
    expiresAt.value = null
    
    tokenRefreshManager?.stop()
    corpAuthCookies.clearSession()
  }

  /**
   * Logout: revoga no servidor (background), limpa sessão local e redireciona ao Auth.
   * Nenhum passo espera o outro — o fetch usa keepalive para sobreviver ao unload da página.
   */
  const logout = (): void => {
    const currentRefreshToken = refreshToken.value
    const logoutHeaders = {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    }
    const loginUrl = getAuthLoginUrl()

    if (currentRefreshToken) {
      void fetch(getLogoutUrl(), {
        method: 'DELETE',
        headers: logoutHeaders,
        body: JSON.stringify({ refresh_token: currentRefreshToken }),
        credentials: 'include',
        keepalive: true,
      }).catch((error) => {
        console.error('[CorpAuth] Server logout failed:', error)
      })
    }

    clearSession()
    window.location.replace(loginUrl)
  }
  
  /**
   * Gera a URL do Auth para login
   */
  const getAuthLoginUrl = (redirectTo?: string): string => {
    const authUrl = import.meta.env.VITE_AUTH_URL
    const redirect = redirectTo || window.location.href
    const encoded = encodeURIComponent(redirect)
    return `${authUrl}/login?redirect=${encoded}&mode=corp`
  }
  
  /**
   * Redireciona para o Auth para fazer login.
   * Backstop: em rotas viewer por token (webview) é no-op — essas rotas são públicas
   * e sem sessão por design, então nunca devem ser redirecionadas ao login.
   */
  const redirectToLogin = (redirectTo?: string) => {
    const currentPath = window.location.pathname || ''
    if (PUBLIC_VIEWER_PATHS.some((viewerPath) => currentPath.startsWith(viewerPath))) return

    clearSession()
    window.location.replace(getAuthLoginUrl(redirectTo))
  }
  
  /**
   * Retorna os headers para requisições autenticadas
   */
  const getAuthHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {}
    
    if (accessToken.value) {
      headers['Authorization'] = `Bearer ${accessToken.value}`
    }
    
    if (accountId.value) {
      headers['X-ACCOUNT-ID'] = accountId.value
    }
    
    return headers
  }
  
  /**
   * Retorna uma instância de fetch autenticado com retry automático
   */
  const getAuthFetch = () => {
    if (!authFetchInstance) {
      authFetchInstance = createAuthFetch({
        getToken: () => accessToken.value || null,
        getRefreshToken: () => refreshToken.value || null,
        refreshUrl: getRefreshUrl(),
        onTokenRefreshed: updateTokens,
        onAuthFailed: () => redirectToLogin(),
        getAdditionalHeaders: () => {
          const headers: Record<string, string> = {}
          if (accountId.value) {
            headers['X-ACCOUNT-ID'] = accountId.value
          }
          return headers
        },
      })
    }
    return authFetchInstance
  }
  
  return {
    // State
    accessToken,
    refreshToken,
    accountId,
    businessCnpj,
    expiresAt,
    isLoading,
    isRefreshing,
    
    // Getters
    isAuthenticated,
    tokenData,
    hasAccountSelected,
    
    // Actions
    loadSession,
    hydrateSession,
    updateTokens,
    completeCreatePassword,
    setAccountId,
    setBusinessCnpj,
    clearAccount,
    forceRefresh,
    clearSession,
    logout,
    getAuthLoginUrl,
    redirectToLogin,
    getAuthHeaders,
    getAuthFetch,
  }
})
